import React, { useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import BoappaLogo from '../../../assets/icons/boappa.svg'
import { theme } from '../../../theme/theme'
import { ResetPasswordForm } from './ResetPasswordForm'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { Button } from '../../../components/common/Button'
import { useAuth } from '../../../hooks/useAuth'
import { reverseUrl } from '../../../navigation/reverseUrl'

export const ResetPasswordView = (): JSX.Element => {
  const { userId } = useAuth()
  // The address the mail was asked for, shown back so that a typo is noticed
  // here rather than after waiting for a mail that went somewhere else. Kept in
  // state, never in the url.
  const [sentToEmail, setSentToEmail] = useState<string | null>(null)
  const { translate } = useAppTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  useDocumentTitle(reverseDocumentTitle('reset-password'))

  // Handed over by the page a password link opens, when the link turned out to
  // be spent or expired. Somebody who arrives that way did not choose to be
  // here, so the reason stays on screen while they are -- a toast would be gone
  // before they looked up.
  const linkWasDead =
    (location.state as { linkWasDead?: boolean } | null)?.linkWasDead === true

  if (userId) {
    return <Navigate to={reverseUrl('login')} />
  }

  return (
    <div className="flex h-full flex-col md:flex-row">
      <BoappaLogo
        className="absolute inset-x-8 w-16 md:w-40"
        alt="boappa logo"
      />
      <div className="mt-24 flex flex-1 justify-center overflow-y-auto md:mt-0 md:items-center">
        <div className="flex w-[90%] flex-col gap-4 md:w-[50%] xl:w-[30%]">
          {sentToEmail != null ? (
            <>
              <p style={theme.textVariants.h1}>
                {translate(
                  'authenticationResetPasswordView.confirmationScreen.title'
                )}
              </p>
              <p style={theme.textVariants.paragraph}>
                {translate(
                  'authenticationResetPasswordView.confirmationScreen.preEmailText'
                )}{' '}
                <span
                  style={theme.textVariants.lead}
                  className="[overflow-wrap:anywhere]"
                >
                  {sentToEmail}
                </span>
              </p>
              <p style={theme.textVariants.paragraph}>
                {translate(
                  'authenticationResetPasswordView.confirmationScreen.instructionText'
                )}
              </p>
              <Button
                style={{
                  ...theme.textVariants.paragraph,
                }}
                className="flex h-14 w-full items-center justify-center"
                label={translate('common.actions.back')}
                onClick={() => navigate(-1)}
              />
            </>
          ) : (
            <>
              <p style={theme.textVariants.h1}>
                {translate(
                  linkWasDead
                    ? 'authenticationResetPasswordView.linkExpiredTitle'
                    : 'authenticationResetPasswordView.title'
                )}
              </p>
              {linkWasDead && (
                <p style={theme.textVariants.paragraph}>
                  {translate(
                    'authenticationResetPasswordView.linkExpiredSubtitle'
                  )}
                </p>
              )}
              <ResetPasswordForm setResetPasswordEmailSent={setSentToEmail} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
